import { RECEIPT_OPTION_DEFAULTS } from '@iws/pos-devices'

import { forwardRef } from 'react'

import { PAYMENT_METHODS } from '@/constants/payment'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { lineNet } from '@/services'
import { useReceiptHeader } from '@/services/printer'
import { PosModel } from '@/types'
import { formatDateTime, formatMoney } from '@/utils'

const Row = ({ l, r, bold }: { l: string; r: string; bold?: boolean }) => (
  <div className={bold ? 'flex justify-between font-semibold' : 'flex justify-between'}>
    <span>{l}</span>
    <span className="tabular-nums">{r}</span>
  </div>
)

/**
 * 80mm thermal-printer layout: monospace, one column, dashed rules. Printed via the `.receipt-print` print styles when
 * the device prints through the browser; device printers get the same content from the `receipt` template (`@iws/pos-devices`).
 */
export const Receipt = forwardRef<HTMLDivElement, { order: PosModel.Order }>(({ order }, ref) => {
  const register = useRegisterContext()
  const header = useReceiptHeader()
  const t = order.totals
  const show = { ...RECEIPT_OPTION_DEFAULTS, ...(header.receipt ?? {}) }
  const logo = show.show_logo ? header.receipt?.logo_url : undefined
  return (
    // eslint-disable-next-line no-restricted-syntax -- an 80mm thermal roll prints black on white at a fixed size, whatever the theme
    <div ref={ref} className="receipt-print mx-auto w-[302px] bg-white px-4 py-5 font-mono text-[11px] leading-4 text-black">
      <div className="text-center">
        {logo && <img src={logo} alt="" className="mx-auto mb-1 max-h-12 w-auto" />}
        {header.name && <p className="text-sm font-bold">{header.name}</p>}
        {show.show_address && header.address && <p>{header.address}</p>}
        {show.show_phone && header.phone && <p>{header.phone}</p>}
        {show.show_email && header.email && <p>{header.email}</p>}
        {show.show_registration_no && header.registration_no && <p>Reg. {header.registration_no}</p>}
        {show.show_tax_id && header.tax_id && <p>BIN {header.tax_id}</p>}
      </div>
      <div className="my-2 border-t border-dashed border-black" />
      <Row l={order.number} r={formatDateTime(order.created_at)} />
      {show.show_staff && <Row l={`${register.name} · ${order.cashier}`} r="" />}
      {show.show_customer && order.customer && <Row l={order.customer.name} r={order.customer.phone} />}
      <div className="my-2 border-t border-dashed border-black" />
      {order.lines.map((l) => (
        <div key={l.product._id} className="mb-1">
          <p>{l.product.name}</p>
          <Row l={`  ${l.quantity} x ${l.product.price.toFixed(2)}${l.discount ? ' (disc)' : ''}`} r={lineNet(l).toFixed(2)} />
        </div>
      ))}
      <div className="my-2 border-t border-dashed border-black" />
      <Row l="Subtotal" r={t.subtotal.toFixed(2)} />
      {t.lineDiscounts + t.orderDiscount > 0 && <Row l="Discount" r={`-${(t.lineDiscounts + t.orderDiscount).toFixed(2)}`} />}
      <Row l={register.tax_rate ? `VAT ${Math.round(register.tax_rate * 100)}%` : 'VAT'} r={t.tax.toFixed(2)} />
      {t.adjustment > 0 && <Row l="Adjustment" r={`-${t.adjustment.toFixed(2)}`} />}
      <Row l="TOTAL" r={formatMoney(t.total)} bold />
      <div className="my-2 border-t border-dashed border-black" />
      {order.payments.map((p, i) => (
        <Row key={i} l={PAYMENT_METHODS.find((m) => m.id === p.method)?.label ?? p.method} r={p.amount.toFixed(2)} />
      ))}
      {order.change > 0 && <Row l="Change" r={order.change.toFixed(2)} bold />}
      <div className="my-2 border-t border-dashed border-black" />
      <p className="text-center">Items: {t.itemCount} · {header.footer || 'Thank you for shopping!'}</p>
      {!!header.return_window_days && (
        <p className="text-center">Exchange within {header.return_window_days} days with receipt.</p>
      )}
      <div className="mx-auto mt-3 h-10 w-48 bg-[repeating-linear-gradient(90deg,#000_0_2px,transparent_2px_4px,#000_4px_5px,transparent_5px_8px)]" aria-hidden />
      <p className="text-center">{order.number}</p>
    </div>
  )
})
Receipt.displayName = 'Receipt'
